import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    Output,
} from '@angular/core';
import { MultiRegistrationConfig, MultiregistrationController, UIParticipant, UIParticipantStatus, UIState } from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';
import { IField } from '../interfaces';
import { CommonModule } from '@angular/common';
import { getMapByGetter } from '../utils.service';
import { ApplyFunctionPipe } from '../apply-function.pipe';
import { DomSanitizer } from '@angular/platform-browser';
import { SharedService } from '../shared.service';
import { TRANSLATION_MAP } from 'src/app/enums';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppDomService } from 'src/app/app-dom.service';
import { firstValueFrom } from 'rxjs';
import { InputFieldCreatorService } from '../input-field-creator.service';

export type MultiRegistrationAction = Pick<
    MultiregistrationController,
    | 'addParticipant'
    | 'removeParticipant'
    | 'updateParticipant'
    | 'setMainParticipantTicket'
    | 'setSubParticipantTicket'
>;
export type MultiRegistrationActionOutput<
    Action extends keyof MultiRegistrationAction
> = {
    action: Action;
    params: Parameters<MultiRegistrationAction[Action]>;
};

@Component({
    selector: 'aza-rws-multi-registration-form',
    imports: [
        CommonModule,
        TranslateModule,

        ApplyFunctionPipe,
    ],
    templateUrl: `./multi-registration-form.component.html`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiRegistrationFormComponent implements AfterViewInit {
    @Input({
        required: true,
    })
    state: UIState;

    @Input({
        required: true,
    })
    allParticipantFields?: IField[];

    @Input({
        required: true,
    })
    config: MultiRegistrationConfig;

    subParticipantFields: IField[] = [];

    private _subParticipantFieldIds?: string[];
    public get subParticipantFieldIds(): string[] {
        return this._subParticipantFieldIds;
    }

    @Input({
        required: true,
    })
    public set subParticipantFieldIds(fieldIds: string[]) {
        this._subParticipantFieldIds = fieldIds;
        const fieldMap = getMapByGetter(this.allParticipantFields, field => field.id);
        this.subParticipantFields = this.subParticipantFieldIds.reduce(
            (result, fieldId) => fieldMap[fieldId] ? [...result, fieldMap[fieldId]] : result,
            [] as IField[]
        );
    }

    @Output()
    action = new EventEmitter<
        MultiRegistrationActionOutput<keyof MultiRegistrationAction>
    >();

    attributeName = {
        ticket_type_id: 'ticket_type_id',
        classNamePrefixSubParticipant: 'multi-registration__sub-participant-',
    };

    constructor(
        private inputFieldCreatorSvc: InputFieldCreatorService,
        private sharedSvc: SharedService,
        private translateSvc: TranslateService,
        private domSvc: AppDomService,
        private sanitizer: DomSanitizer,
        private host: ElementRef<HTMLElement>,
        private ngZone: NgZone,
    ) {}

    async ngAfterViewInit() {
        await this.fillAllInputFieldElements();
    }

    createInputFieldElement = (dummyValue: null, field: IField, participant: UIParticipant, validateFnName = 'azavista.validateSubParticipantField') => {
        field = {
            ...field,
            editable: this.getFieldEditableByStatus(participant.status)
        };
        const fieldHtml = this.inputFieldCreatorSvc.createInputFieldBlock(field).replace(
            this.inputFieldCreatorSvc['validateFnName'],
            validateFnName
        );
        return this.sanitizer.bypassSecurityTrustHtml(
            fieldHtml
        );
    };

    createTicketField = (participant: UIParticipant): IField => {
        const { status, ticket_options } = participant;
        return {
            label: this.sharedSvc.translate(TRANSLATION_MAP.TICKET),
            name: this.attributeName.ticket_type_id,
            id: this.attributeName.ticket_type_id,
            editable: this.getFieldEditableByStatus(status),
            schema: {
                type: ['string', 'null'],
                enum: ticket_options?.map(({value}) => value) || []
            },
            attributeTranslations: ticket_options?.map(ticket => ({
                value: ticket.value || '',
                trans: {
                    [this.translateSvc.currentLang]: ticket.label
                }
            })),
        } as IField;
    };

    private async fillAllInputFieldElements() {
        await firstValueFrom(this.ngZone.onStable);
        this.state.subparticipants.forEach(subParticipant => {
            this.fillSubParticipantField(subParticipant);
        });
        this.fillSubParticipantField(this.state.main_participant);
    }

    getFieldEditableByStatus = (status: UIParticipantStatus): boolean => {
        return !(status === UIParticipantStatus.CANCELLED || status === UIParticipantStatus.SCANNED);
    };

    private fillSubParticipantField(subParticipant: UIParticipant) {
        const subParticipantEl: HTMLElement = this.host.nativeElement.querySelector(`.${this.attributeName.classNamePrefixSubParticipant}${subParticipant.id}`);
        if (subParticipant) {
            const subParticipantWithTicket = {
                id: subParticipant.id,
                ...subParticipant.participant_attributes,
                [this.attributeName.ticket_type_id]: subParticipant.ticket_type_id,
            };
            const subParticipantFieldsWithTicketField = this.subParticipantFields.concat([
                this.createTicketField(subParticipant)
            ]);
            this.domSvc.fillInputElements(subParticipantWithTicket, subParticipantFieldsWithTicketField, subParticipantEl);
            this.domSvc.setLockedFields(subParticipantFieldsWithTicketField, subParticipantWithTicket, subParticipantEl);
        }
    }

    emitAction<Action extends keyof MultiRegistrationAction>(output: MultiRegistrationActionOutput<Action>) {
        this.action.emit(output);
    }

    removeSubParticipant(id: string) {
        const output : MultiRegistrationActionOutput<'removeParticipant'> = {
            action: 'removeParticipant',
            params: [id]
        };
        this.emitAction(output);
    }

    addSubParticipant() {
        this.emitAction({
            action: 'addParticipant',
            params: [{}]
        });
    }

    trackByParticipant = (index: number, participant: UIParticipant) => {
        return participant.id;
    };

    recallFillElementOnStatusChanges = (status: UIParticipantStatus, participant: UIParticipant) => {
        (async () => {
            await firstValueFrom(this.ngZone.onStable);
            this.fillSubParticipantField(participant);
        })();
    };
}
